import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Segment, 
    Card,
    Button,
    Image,
    Transition,
    Reveal,
    Dimmer,
    Header,
    Icon
} from 'semantic-ui-react'
import { 
    useLeafletContext, 
    createElementHook,
    useLayerLifecycle 
} from '@react-leaflet/core'
import L from 'leaflet';
import useOnclickOutside from "react-cool-onclickoutside";
import { FaMap } from 'react-icons/fa';

import { Trans } from '@lingui/macro';

const POSITION_CLASSES = {
    bottomleft: 'leaflet-bottom leaflet-left',
    bottomright: 'leaflet-bottom leaflet-right',
    topleft: 'leaflet-top leaflet-left',
    topright: 'leaflet-top leaflet-right',
}

export const BASEMAPS = [
    {
        key: 'CartoDB: Light',
        name: 'CartoDB Light',
        url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png?key=cb1_3nim_1_c550155b134f9fb224c2a8d0',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
    {
        key: 'CartoDB: Dark',
        name: 'CartoDB Dark',
        url: 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png?key=cb1_3nim_1_c550155b134f9fb224c2a8d0',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
    {
        key: 'ESRI: World Shaded Relief',
        name: 'ESRI World Shaded Relief',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri &mdash; Source: ESRI'
    },
    {
        key: 'ESRI: World Physical',
        name: 'ESRI World Physical',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Physical_Map/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri &mdash; Source: US National Park Service',
    },
    {
        key: 'ESRI: World Imagery',
        name: 'ESRI World Imagery',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
    },
    
]

export const LABELS = [
    {
        key: 'CartoDB: Light Labels',
        name: 'CartoDB Light Labels',
        url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png?key=cb1_3nim_1_c550155b134f9fb224c2a8d0',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
    {
        key: 'CartoDB: Dark Labels',
        name: 'CartoDB Dark Labels',
        url: 'https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png?key=cb1_3nim_1_c550155b134f9fb224c2a8d0',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    },
]


export const BasemapControl = () => {
    const state = useSelector((state) => state.explore);
    const dispatch = useDispatch();

    const [open, openBasemaps] = useState(false)

    const ref = useOnclickOutside(() => {
        openBasemaps(false);
    });

    const toggleBasemaps = () => {
        openBasemaps(!open);
    };

    const getBasemapThumb = (url) => {
        let thumb = url.replace('{s}', 'a')
        thumb = thumb.replace('{z}', '0')
        thumb = thumb.replace('{x}', '0')
        thumb = thumb.replace('{y}', '0')
        return thumb
    }
    
    const changeBasemap = (e, data) => {
        console.log(data.data);
        console.log(state.activeBasemap.url)
        dispatch({type: 'activeBasemap', value: data.data });
    };

    const basemapCards = BASEMAPS.map((basemap) => {
        return (
            <Card key={basemap['key']} data={basemap} raised onClick={changeBasemap}>
                <Reveal animated='fade'>
                    <Reveal.Content visible>
                        <Image src={getBasemapThumb(basemap.url)} />
                    </Reveal.Content>
                    <Reveal.Content hidden>
                        <Dimmer.Dimmable dimmed={true}>
                            <Dimmer active={true}>
                                <Header as='h5' inverted>{basemap.name}</Header>
                            </Dimmer>
                            <Image src={getBasemapThumb(basemap.url)} />
                        </Dimmer.Dimmable>
                    </Reveal.Content>
                </Reveal>
            </Card>
        )
    })

    return ( 
        <div className={POSITION_CLASSES['bottomright']}>
            <div ref={ref} className="leaflet-control">
                <div className="basemapControl">
                    <Button
                        className="white basemapsButton leafletButton"
                        onClick={toggleBasemaps} fluid
                        icon labelPosition='left' size='small'
                    >
                        <Icon>
                        <FaMap size="1.5em" className="basemapIcon"/><br/>
                        </Icon>
                        <span style={{paddingLeft:'1.5rem'}}><Trans>Basemaps</Trans></span>
                    </Button>
                    <Transition visible={open} animation='fade' duration={250}>
                        <div>
                        <Segment raised className="basemapOptions">
                            <Card.Group className="white" itemsPerRow={2}>
                                {basemapCards}
                            </Card.Group>
                        </Segment>
                        </div>
                    </Transition>
                </div>
            </div>
        </div>
    );
}


// BASEMAP LAYER

const getBasemapUrl = (state) => {
    let basemapUrl = state.activeBasemap.url
    
    return basemapUrl
}

const createBasemap = (state, dispatch, context) => {
    return { instance: new L.TileLayer(getBasemapUrl(state), {
        "type": "Basemap"
    })
        .bringToBack()
    }
}

const updateBasemap = (instance, state, prevState) => {
    if (state.activeBasemap !== prevState.activeBasemap) {
        instance.setUrl(getBasemapUrl(state));
        instance.bringToBack()
    } 

}

const useBasemapElement = createElementHook(createBasemap, updateBasemap)

export const Basemap = () => {
    const state = useSelector((state) => state.explore);
    const dispatch = useDispatch();

    const context = useLeafletContext()
    const elementRef = useBasemapElement(state, dispatch, context)
    useLayerLifecycle(elementRef.current, context)

    return null
}

const getLabelUrl = (state) => {
    let mapLabelsUrl = state.activeLabels.url
    
    return mapLabelsUrl
}

const createLabel = (state, dispatch, context) => {
    return { instance: new L.TileLayer(getLabelUrl(state), {
        pane: 'popupPane'
    })
        .bringToFront()
    }
}

const updateLabel = (instance, state, prevState) => {
    if (state.activeLabels !== prevState.activeLabels) {
        instance.setUrl(getLabelUrl(state))
    } 

}

const useLabelElement = createElementHook(createLabel, updateLabel)

export const MapLabels = () => {
    const state = useSelector((state) => state.explore);
    const dispatch = useDispatch();

    const context = useLeafletContext()
    const elementRef = useLabelElement(state, dispatch, context)
    useLayerLifecycle(elementRef.current, context)

    return null
}